import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Product, Store, WorkspaceView } from '../types';
import {
  computeEconomics,
  formatMoney,
  getAmazonStatusMeta,
  getAmazonProductUrl,
  getSellerCentralSkuUrl,
  computeClientIssueSummary,
  classifySync,
  formatSyncDiffSummary,
  SYNC_STAGE_LABEL,
  buildProductFixPrompt,
  buildStagePillFixPrompt,
  copyTextToClipboard,
  showToast
} from '../utils';

interface ProductsViewProps {
  active: boolean;
  products: Product[];
  stores: Store[];
  activeStore: Store | null;
  activeStoreId: string;
  selectedProduct: Product | null;
  activeFilter: string;
  onChangeFilter: (filter: string) => void;
  onResetFilterAndStore: () => void;
  onSwitchView: (view: WorkspaceView) => void;
  onSelectProductAndView: (productId: string, view: WorkspaceView, scrollToDossier?: boolean) => void;
  onOpenLightbox: (product: Product, triggerEl: HTMLElement | null) => void;
  onOpenIssues: (productId: string, triggerEl: HTMLElement | null) => void;
  onSyncSingleProductCard: (product: Product, triggerEl: HTMLElement | null) => Promise<void>;
  onQuickUpdateProduct: (
    productId: string,
    payload: { amazonRetailPrice: number; fbmQuantity: number; syncNow: boolean }
  ) => Promise<void>;
  onPullAmazon: () => Promise<void>;
  onSyncAllAmazon: (triggerEl: HTMLElement | null) => Promise<void>;
  onPatchAllAmazon?: (productIds?: string[]) => Promise<void>;
  onScanKeyword: (keyword: string) => Promise<void>;
  syncInFlight: boolean;
  pullStatusLine: { text: string; className: string };
  lastPullError: string | null;
  sellerId: string;
}

const FILTER_CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'Tech', label: 'Tech & Desk' },
  { key: 'Home', label: 'Home & Coffee' },
  { key: 'Beauty', label: 'Beauty' },
  { key: 'Pet', label: 'Pet' }
];

export const ProductsView: React.FC<ProductsViewProps> = ({
  active,
  products,
  stores,
  activeStore,
  activeStoreId,
  selectedProduct,
  activeFilter,
  onChangeFilter,
  onResetFilterAndStore,
  onSwitchView,
  onSelectProductAndView,
  onOpenLightbox,
  onOpenIssues,
  onSyncSingleProductCard,
  onQuickUpdateProduct,
  onPullAmazon,
  onSyncAllAmazon,
  onPatchAllAmazon,
  onScanKeyword,
  syncInFlight,
  pullStatusLine,
  lastPullError,
  sellerId
}) => {
  const [scanKeyword, setScanKeyword] = useState('');
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [scanError, setScanError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [pullLoading, setPullLoading] = useState(false);
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [patchAllLoading, setPatchAllLoading] = useState(false);
  const [syncingCardIds, setSyncingCardIds] = useState<Record<string, boolean>>({});
  const [activeCardImages, setActiveCardImages] = useState<Record<string, string>>({});
  const [activeStageFilter, setActiveStageFilter] = useState<string>('all');
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [quickPrice, setQuickPrice] = useState<string>('');
  const [quickQty, setQuickQty] = useState<string>('50');
  const [quickSavingId, setQuickSavingId] = useState<string | null>(null);
  const [copiedStagePrompt, setCopiedStagePrompt] = useState<boolean>(false);
  const [copiedCardPromptId, setCopiedCardPromptId] = useState<string | null>(null);
  const [layoutMode, setLayoutMode] = useState<'grid' | 'list' | 'iphone'>(() => {
    try {
      if (typeof window !== 'undefined') {
        if (window.location.search.includes('layout=iphone')) return 'iphone';
        if (window.location.search.includes('layout=list')) return 'list';
        if (window.location.search.includes('layout=grid')) return 'grid';
      }
      const saved = localStorage.getItem('aurelius_catalog_layout');
      if (saved === 'grid' || saved === 'list' || saved === 'iphone') return saved;
      return 'grid';
    } catch (_) {
      return 'grid';
    }
  });
  const scanInputRef = useRef<HTMLInputElement>(null);
  const CATALOG_PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState<number>(CATALOG_PAGE_SIZE);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const pendingSyncRows = products
    .map((p) => ({ p, ...classifySync(p) }))
    .filter((r) => ['drift', 'notsent', 'failed', 'stuck'].includes(r.stage));

  const heroProd = selectedProduct || products[0];
  const heroEcon = heroProd ? computeEconomics(heroProd, activeStore) : null;
  const heroUsDropCost = heroProd
    ? heroProd.researchDossier?.usDropshipCost || +((heroEcon?.landedUnitCost || 8) + 5.5).toFixed(2)
    : 14.5;

  const totalRev = activeStore
    ? activeStore.monthlyRevenueEst || 28000
    : stores.reduce((sum, s) => sum + (Number(s.monthlyRevenueEst) || 0), 0);

  const activeSearchTerm = (catalogSearchQuery || scanKeyword).trim().toLowerCase();

  const filteredProducts = products.filter((p) => {
    if (activeSearchTerm) {
      const sku = (p.amazonListing?.amazonSku || p.sku || '').toLowerCase();
      const asin = (p.amazonListing?.asin || p.asin || '').toLowerCase();
      const cjSku = (p.cjSupplier?.cjSku || p.researchDossier?.cjSku || '').toLowerCase();
      const cjPid = (p.cjSupplier?.pid || p.researchDossier?.cjPid || '').toLowerCase();
      const title = (p.title || '').toLowerCase();
      const niche = (p.niche || '').toLowerCase();
      const rank = `#${p.rank}`.toLowerCase();
      const id = (p.id || '').toLowerCase();
      const matchesQuery =
        sku.includes(activeSearchTerm) ||
        asin.includes(activeSearchTerm) ||
        cjSku.includes(activeSearchTerm) ||
        cjPid.includes(activeSearchTerm) ||
        title.includes(activeSearchTerm) ||
        niche.includes(activeSearchTerm) ||
        rank.includes(activeSearchTerm) ||
        id.includes(activeSearchTerm);
      if (!matchesQuery) return false;
    }
    if (activeFilter !== 'all' && !p.niche.toLowerCase().includes(activeFilter.toLowerCase())) {
      return false;
    }
    if (activeStoreId !== 'all' && Array.isArray(p.assignedStores)) {
      if (!p.assignedStores.includes(activeStoreId)) return false;
    }
    if (activeStageFilter !== 'all' && classifySync(p).stage !== activeStageFilter) {
      return false;
    }
    return true;
  });

  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const hasMoreProducts = visibleCount < filteredProducts.length;

  useEffect(() => {
    setVisibleCount(CATALOG_PAGE_SIZE);
  }, [activeFilter, activeStoreId, activeStageFilter, layoutMode, activeSearchTerm]);

  const fetchNextBatch = useCallback(() => {
    setIsFetchingMore(true);
    window.setTimeout(() => {
      setVisibleCount((prev) => Math.min(filteredProducts.length, prev + CATALOG_PAGE_SIZE));
      setIsFetchingMore(false);
    }, 140);
  }, [filteredProducts.length]);

  useEffect(() => {
    if (!active || !hasMoreProducts) return;
    const node = loadMoreSentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingMore) {
          fetchNextBatch();
        }
      },
      { rootMargin: '320px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, hasMoreProducts, isFetchingMore, fetchNextBatch]);

  const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const handleScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = scanKeyword.trim();
    if (!trimmed) {
      setScanError(true);
      scanInputRef.current?.focus();
      return;
    }
    setScanError(false);
    const q = trimmed.toLowerCase();
    const existingMatch = products.find(
      (p) =>
        (p.amazonListing?.amazonSku || p.sku || '').toLowerCase() === q ||
        (p.amazonListing?.asin || p.asin || '').toLowerCase() === q ||
        (p.cjSupplier?.cjSku || p.researchDossier?.cjSku || '').toLowerCase() === q ||
        (p.cjSupplier?.pid || '').toLowerCase() === q ||
        (p.id || '').toLowerCase() === q
    );
    if (existingMatch) {
      setCatalogSearchQuery(trimmed);
      onSelectProductAndView(existingMatch.id, 'products');
      setTimeout(() => {
        document.getElementById('product-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
      showToast(`Found ${existingMatch.amazonListing?.amazonSku || existingMatch.id}: ${existingMatch.title}`);
      return;
    }
    setScanning(true);
    try {
      await onScanKeyword(trimmed);
      setScanKeyword('');
    } finally {
      setScanning(false);
    }
  };

  const renderSyncProgressSection = () => {
    if (!isLocal || !products.length) return <section id="sync-progress" className="sync-progress" aria-live="polite" hidden></section>;

    const rows = products.map((p) => ({ p, ...classifySync(p) }));
    const total = rows.length;
    const count = (stage: string) => rows.filter((r) => r.stage === stage).length;
    const listed = count('listed');
    const review = count('review');
    const confirmed = count('confirmed') + listed + review;
    const driftRows = rows.filter((r) => r.stage === 'drift');
    const problems = rows.filter((r) => ['failed', 'stuck', 'attention'].includes(r.stage));
    const pending = count('pending');
    const notsent = count('notsent');
    const pct = Math.round((confirmed / total) * 100);

    let tone = 'is-idle';
    let headline = '';
    if (syncInFlight) {
      tone = 'is-active';
      headline = 'Sending to Amazon…';
    } else if (problems.length) {
      tone = 'is-problem';
      headline = `${problems.length} product${problems.length === 1 ? '' : 's'} didn't reach Amazon cleanly`;
    } else if (driftRows.length) {
      tone = 'is-active';
      headline = `${driftRows.length} local update${driftRows.length === 1 ? '' : 's'} ready to sync to Amazon`;
    } else if (confirmed === total && (listed || review)) {
      tone = 'is-done';
      const parts = [`${count('confirmed')} active & buyable`];
      if (listed) parts.push(`${listed} indexing offer`);
      if (review) parts.push(`${review} in 48-hr Amazon review`);
      headline = `All ${total} products synced to Amazon · ${parts.join(' · ')}`;
    } else if (confirmed === total) {
      tone = 'is-done';
      headline = `All ${total} products active & buyable on Amazon`;
    } else if (pending) {
      tone = 'is-active';
      headline = `${confirmed}/${total} synced · ${pending} waiting for Amazon`;
    } else {
      headline = `${notsent} product${notsent === 1 ? '' : 's'} not sent yet. Press ⚡ Sync to Amazon.`;
    }

    const stageOrder = ['confirmed', 'listed', 'review', 'drift', 'pending', 'attention', 'failed', 'stuck', 'notsent'];
    const groupedStages = stageOrder
      .map((stage) => {
        const matching = rows.filter((r) => r.stage === stage);
        return { stage, count: matching.length, items: matching };
      })
      .filter((g) => g.count > 0);

    const alerts: { title: string; msg: string; key: string }[] = [];
    if (lastPullError) {
      alerts.push({ title: 'Cannot reach Amazon', msg: lastPullError, key: 'pull:' + lastPullError });
    }
    for (const r of problems) {
      alerts.push({
        title: r.p.title.length > 56 ? r.p.title.slice(0, 54) + '…' : r.p.title,
        msg: r.msg || '',
        key: `${r.p.id}:${r.stage}:${r.msg}`
      });
    }

    return (
      <section id="sync-progress" className={`sync-progress ${tone}`} aria-live="polite">
        <div className="sync-progress-head">
          <strong className="sync-progress-title">{headline}</strong>
          <span className="sync-progress-count tabular">
            {confirmed}/{total} accepted
          </span>
        </div>
        <div
          className="sync-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={confirmed}
          aria-label="Products accepted by Amazon"
        >
          <div className="sync-bar-fill" style={{ width: `${pct}%` }}></div>
          {(syncInFlight || pending > 0) && <div className="sync-bar-sheen" aria-hidden="true"></div>}
        </div>
        <ul className="sync-chips" role="group" aria-label="Filter products by Amazon sync status">
          <li>
            <button
              type="button"
              className={`sync-chip stage-all ${activeStageFilter === 'all' ? 'is-selected' : ''}`}
              aria-pressed={activeStageFilter === 'all' ? 'true' : 'false'}
              title="Show all products"
              onClick={() => setActiveStageFilter('all')}
            >
              <span className="sync-chip-idx tabular">{total}</span>
              <span className="sync-chip-label">All products</span>
            </button>
          </li>
          {groupedStages.map((g) => {
            const skuList = g.items.map((r) => `#${String(r.p.rank || r.p.id.slice(-2)).padStart(2, '0')} ${r.p.title}`).join('\n');
            const isSelected = activeStageFilter === g.stage;
            return (
              <li key={g.stage}>
                <button
                  type="button"
                  className={`sync-chip stage-${g.stage} ${isSelected ? 'is-selected' : ''}`}
                  aria-pressed={isSelected ? 'true' : 'false'}
                  title={`${SYNC_STAGE_LABEL[g.stage]} (${g.count}) — click to filter:\n${skuList}`}
                  onClick={() => setActiveStageFilter((prev) => (prev === g.stage ? 'all' : g.stage))}
                >
                  <span className="sync-chip-idx tabular">{g.count}</span>
                  <span className="sync-chip-label">{SYNC_STAGE_LABEL[g.stage]}</span>
                </button>
              </li>
            );
          })}
          {rows.some((r) => r.stage !== 'confirmed') && (
            <li style={{ marginLeft: 'auto' }}>
              <button
                type="button"
                id="copy-pill-fix-prompt-chip-btn"
                className="tabular"
                style={{
                  background: copiedStagePrompt ? '#15803D' : '#09090B',
                  color: '#FFFFFF',
                  border: '1px solid #D9B668',
                  borderRadius: '999px',
                  padding: '6px 14px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Copy a ready-to-paste Antigravity prompt to fix the issues in the selected pill (or all issue pills)"
                onClick={async () => {
                  const selectedGroup = groupedStages.find((g) => g.stage === activeStageFilter && g.stage !== 'confirmed');
                  const targetItems = selectedGroup
                    ? selectedGroup.items
                    : rows.filter((r) => r.stage !== 'confirmed');
                  const targetLabel = selectedGroup
                    ? SYNC_STAGE_LABEL[selectedGroup.stage] || selectedGroup.stage
                    : 'All Non-Active / Issue Pills';
                  const targetKey = selectedGroup ? selectedGroup.stage : 'all_issues';
                  const promptText = buildStagePillFixPrompt(targetKey, targetLabel, targetItems);
                  const ok = await copyTextToClipboard(promptText);
                  if (ok) {
                    setCopiedStagePrompt(true);
                    showToast(`📋 Copied Fix Prompt for ${targetItems.length} product(s) in "${targetLabel}" — paste into Antigravity!`);
                    setTimeout(() => setCopiedStagePrompt(false), 2600);
                  }
                }}
              >
                <span>
                  {copiedStagePrompt
                    ? '✓ Copied Fix Prompt!'
                    : activeStageFilter !== 'all' && activeStageFilter !== 'confirmed'
                      ? `📋 Copy Fix Prompt (${SYNC_STAGE_LABEL[activeStageFilter]})`
                      : '📋 Copy Fix Prompt'}
                </span>
              </button>
            </li>
          )}
        </ul>
        {activeStageFilter !== 'all' &&
          activeStageFilter !== 'confirmed' &&
          (() => {
            const activeGroup = groupedStages.find((g) => g.stage === activeStageFilter);
            if (!activeGroup || !activeGroup.items.length) return null;
            const stageLabel = SYNC_STAGE_LABEL[activeStageFilter] || activeStageFilter;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginTop: 10,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: '#FAF6EC',
                  border: '1px solid #D9B668'
                }}
              >
                <div style={{ fontSize: '0.84rem', color: '#09090B', fontWeight: 600 }}>
                  Viewing <strong>{activeGroup.count}</strong> product{activeGroup.count === 1 ? '' : 's'} in pill{' '}
                  <strong>&ldquo;{stageLabel}&rdquo;</strong> — click <strong>Copy Fix Prompt</strong> and paste it into Antigravity to fix them automatically.
                </div>
                <button
                  type="button"
                  id="copy-selected-stage-prompt-btn"
                  className="tabular"
                  style={{
                    background: copiedStagePrompt ? '#15803D' : '#09090B',
                    color: '#FFFFFF',
                    border: '1px solid #09090B',
                    borderRadius: 8,
                    padding: '7px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  onClick={async () => {
                    const promptText = buildStagePillFixPrompt(activeStageFilter, stageLabel, activeGroup.items);
                    const ok = await copyTextToClipboard(promptText);
                    if (ok) {
                      setCopiedStagePrompt(true);
                      showToast(`📋 Copied Fix Prompt for ${activeGroup.count} product(s) in "${stageLabel}" — paste into Antigravity!`);
                      setTimeout(() => setCopiedStagePrompt(false), 2600);
                    }
                  }}
                >
                  {copiedStagePrompt ? '✓ Copied Fix Prompt!' : `📋 Copy Fix Prompt (${activeGroup.count})`}
                </button>
              </div>
            );
          })()}
        {driftRows.length > 0 && (
          <div className="sync-drift-container">
            <div
              className="sync-drift-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                marginTop: '12px',
                marginBottom: '8px',
                background: '#09090B',
                color: '#FFFFFF',
                borderRadius: '10px',
                border: '1px solid #27272A'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span
                  className="tabular"
                  style={{
                    background: '#D9B668',
                    color: '#09090B',
                    fontWeight: 800,
                    fontSize: '0.76rem',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase'
                  }}
                >
                  {driftRows.length} Pending Patch{driftRows.length === 1 ? '' : 'es'}
                </span>
                <span style={{ fontSize: '0.86rem', color: '#E4E4E7', fontWeight: 500 }}>
                  Batch-apply all queued attribute, image, title, price &amp; stock updates to Amazon SP-API in one click
                </span>
              </div>
              <button
                type="button"
                id="patch-all-drift-btn"
                className="tabular"
                disabled={patchAllLoading || syncAllLoading}
                style={{
                  background: '#FFFFFF',
                  color: '#09090B',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  padding: '9px 18px',
                  borderRadius: '8px',
                  border: '1px solid #D9B668',
                  cursor: patchAllLoading || syncAllLoading ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                }}
                onClick={async () => {
                  setPatchAllLoading(true);
                  try {
                    if (onPatchAllAmazon) {
                      await onPatchAllAmazon(driftRows.map((r) => r.p.id));
                    } else {
                      await onSyncAllAmazon(null);
                    }
                  } finally {
                    setPatchAllLoading(false);
                  }
                }}
              >
                {patchAllLoading
                  ? `⚡ Patching All ${driftRows.length} Listings…`
                  : `⚡ Fast PATCH All (${driftRows.length}) Now`}
              </button>
            </div>
            <ul className="sync-drift-queue" aria-label="Pending Amazon updates" style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {driftRows.map((r) => {
                const idx = String(r.p.rank || r.p.id.slice(-2)).padStart(2, '0');
                const isSaving = quickSavingId === r.p.id || patchAllLoading;
                return (
                  <li key={r.p.id} className="sync-drift-item">
                    <div className="sync-drift-meta">
                      <span className="sync-drift-sku tabular">#{idx} · {r.p.amazonListing?.amazonSku || r.p.sku}</span>
                      <strong className="sync-drift-title truncate">{r.p.title}</strong>
                      <span className="sync-drift-diff tabular">{r.msg || formatSyncDiffSummary(r.p)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn-jet btn-xs tabular"
                      disabled={isSaving}
                      onClick={async () => {
                        setQuickSavingId(r.p.id);
                        try {
                          await onQuickUpdateProduct(r.p.id, {
                            amazonRetailPrice: Number(r.p.pricing.amazonRetailPrice),
                            fbmQuantity: Number(r.p.amazonListing?.fbmQuantity ?? 50),
                            syncNow: true
                          });
                        } finally {
                          setQuickSavingId(null);
                        }
                      }}
                    >
                      {isSaving ? 'Patching…' : '⚡ Fast PATCH Now'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {alerts.length > 0 && (
          <ul className="sync-alerts" role="alert">
            {alerts.map((a) => (
              <li key={a.key}>
                <strong>{a.title}</strong>
                <span>{a.msg}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  };

  const renderIssuePill = (p: Product) => {
    const s = p.channelIssues ? computeClientIssueSummary(p, stores) : p.issueSummary;
    const amzErrs = (p.channelIssues?.['store-amz-us']?.issues || []).filter((i) => i.severity === 'error');
    if (s && amzErrs.length && s.errorCount === amzErrs.length && amzErrs.every((i) => String(i.code) === '100521')) {
      return (
        <button
          type="button"
          className="card-health-pill open-issues-btn has-warnings"
          data-product-id={p.id}
          onClick={(e) => onOpenIssues(p.id, e.currentTarget)}
          title="Amazon error 100521: listing hidden while Amazon reviews it (up to 48h)"
          aria-label={`Amazon is reviewing ${p.title}: listing hidden for up to 48 hours. View details`}
        >
          <span className="health-icon" aria-hidden="true">
            ⏳
          </span>
          <span className="health-label tabular">1 Amazon hold · under review</span>
          <span className="health-arrow">
            View details <span aria-hidden="true">→</span>
          </span>
        </button>
      );
    }

    if (!s) {
      return (
        <button
          type="button"
          className="card-health-pill open-issues-btn is-evaluating"
          data-product-id={p.id}
          onClick={(e) => onOpenIssues(p.id, e.currentTarget)}
          aria-label={`Checking listing health for ${p.title}`}
        >
          <span className="health-dot dot-evaluating"></span>
          <span className="health-label">Checking channel health…</span>
          <span className="health-arrow" aria-hidden="true">
            →
          </span>
        </button>
      );
    }

    const tone = s.errorCount > 0 ? 'has-errors' : s.warningCount > 0 ? 'has-warnings' : 'is-clean';
    const icon = s.errorCount > 0 ? '⚠' : s.warningCount > 0 ? '!' : '✓';
    const parts: string[] = [];
    if (s.errorCount) parts.push(`${s.errorCount} error${s.errorCount === 1 ? '' : 's'}`);
    if (s.warningCount) parts.push(`${s.warningCount} warning${s.warningCount === 1 ? '' : 's'}`);
    const fullLabel = parts.length ? parts.join(' · ') : 'All channels verified healthy';
    const storesText = s.storesWithErrors ? ` across ${s.storesWithErrors} store${s.storesWithErrors === 1 ? '' : 's'}` : '';
    const shortLabel = s.errorCount
      ? `${parts[0]}${s.storesWithErrors ? ` · ${s.storesWithErrors} store${s.storesWithErrors === 1 ? '' : 's'}` : ''}`
      : s.warningCount
      ? `${parts[0]} to review`
      : 'Channel Health: 100% Ready (0 issues)';

    return (
      <button
        type="button"
        className={`card-health-pill open-issues-btn ${tone}`}
        data-product-id={p.id}
        onClick={(e) => onOpenIssues(p.id, e.currentTarget)}
        title={`${fullLabel}${storesText}`}
        aria-label={`View all listing issues for ${p.title}: ${fullLabel}${storesText}`}
      >
        <span className="health-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="health-label tabular">{shortLabel}</span>
        <span className="health-arrow">
          View details <span aria-hidden="true">→</span>
        </span>
      </button>
    );
  };

  return (
    <div id="view-products" className={`workspace-view ${active ? 'active' : ''}`} role="tabpanel" aria-labelledby="tab-products" hidden={!active}>
      <section className="hero-split">
        <div className="container hero-grid">
          <div className="hero-copy">
            <span className="eyebrow-tag" id="hero-eyebrow">
              {activeStore
                ? `ACTIVE STOREFRONT · ${activeStore.name.toUpperCase()} (${activeStore.channel.toUpperCase()})`
                : '100% RESEARCH VERIFIED · US DOMESTIC DROPSHIP ($0 STOCK) OR ALIBABA FBA'}
            </span>
            <h1>
              {products.length} Researched Winners.{' '}
              <span className="gold-highlight" id="hero-headline-gold">
                Sell With $0 Inventory or Bulk FBA.
              </span>
            </h1>
            <p className="hero-lead">
              Every product below passed a 14,200-ASIN market audit for high search demand, low competitor reviews, and 42%+ margins. Start with{' '}
              <strong>$0 upfront inventory via US Domestic Dropship</strong> (no Alibaba required) or scale with private-label FBA.
            </p>

            <form id="alibaba-scan-form" className="hero-search-wrap" noValidate onSubmit={handleScanSubmit}>
              <div className="hero-search-form">
                <label htmlFor="scan-keyword" className="sr-only">
                  Search Product Idea
                </label>
                <input
                  ref={scanInputRef}
                  type="text"
                  id="scan-keyword"
                  name="keyword"
                  required
                  autoComplete="off"
                  aria-invalid={scanError ? 'true' : undefined}
                  aria-describedby="scan-keyword-error"
                  placeholder="Research a new product niche (e.g., 'magnetic travel charger')..."
                  value={scanKeyword}
                  onChange={(e) => {
                    setScanKeyword(e.target.value);
                    if (scanError) setScanError(false);
                  }}
                />
                <button type="submit" className="btn-gold" id="scan-submit-btn" disabled={scanning} aria-busy={scanning ? 'true' : undefined}>
                  {scanning ? 'Scanning...' : 'Research Niche'}
                </button>
                <button type="button" className="btn-white-sm" id="hero-open-research-btn" onClick={() => onSwitchView('research')}>
                  📊 Read Research Dossier →
                </button>
              </div>
              <p id="scan-keyword-error" className="field-error hero-field-error" role="alert" hidden={!scanError}>
                Enter a product keyword or niche to run a market &amp; margin audit.
              </p>
            </form>

            <div className="hero-metrics-strip">
              <div className="hero-metric">
                <span className="metric-label">RESEARCH AUDIT</span>
                <span className="metric-value tabular" id="hero-metric-stores">
                  {activeStore ? `${activeStore.referralRateLabel} Fee` : '100% Verified'}
                </span>
              </div>
              <div className="hero-metric">
                <span className="metric-label">AVG NET MARGIN</span>
                <span className="metric-value tabular" id="hero-metric-margin">
                  {heroEcon ? `${heroEcon.netMarginPct}%` : '46.8%'}
                </span>
              </div>
              <div className="hero-metric">
                <span className="metric-label">EST. MONTHLY REVENUE</span>
                <span className="metric-value tabular" id="hero-metric-rev">
                  ${totalRev.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {heroProd && heroEcon && (
            <div className="hero-showcase-frame">
              <div className="showcase-badge-top">
                <span id="hero-card-rank" className="tabular">
                  RESEARCH PICK #{heroProd.rank}
                </span>
                <span className="gold-pill tabular" id="hero-card-net">
                  +{formatMoney(heroEcon.netProfitPerUnit)} Profit / Unit
                </span>
              </div>
              <div className="hero-image-stage">
                <img
                  id="hero-featured-img"
                  src={heroProd.images[0]}
                  alt={heroProd.title}
                  fetchPriority="high"
                />
              </div>
              <div className="showcase-caption-bar">
                <div className="caption-meta">
                  <div className="caption-title truncate" id="hero-card-title">
                    {heroProd.title}
                  </div>
                  <div className="caption-kicker tabular" id="hero-card-kicker">
                    US Dropship: {formatMoney(heroUsDropCost)} ($0 MOQ) · Alibaba: {formatMoney(heroEcon.landedUnitCost)} · Retail:{' '}
                    {formatMoney(heroProd.pricing.amazonRetailPrice)}
                  </div>
                </div>
                <button type="button" className="btn-white-sm" id="hero-inspect-btn" onClick={() => onSwitchView('launch')}>
                  Launch <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="concierge-dock" aria-label="How It Works">
        <div className="container dock-grid">
          <div className="dock-item">
            <span className="dock-num tabular">01</span>
            <h3>100% Verified Market Research</h3>
            <p>Screened 14,200+ ASINs for high search velocity, low review moats &amp; 1-star competitor gaps.</p>
          </div>
          <div className="dock-item">
            <span className="dock-num tabular">02</span>
            <h3>No Alibaba Inventory Required</h3>
            <p>Launch immediately with $0 upfront stock using US Domestic 3PL Dropship (2–4 day delivery).</p>
          </div>
          <div className="dock-item">
            <span className="dock-num tabular">03</span>
            <h3>Sync to All 5 Storefronts</h3>
            <p>Syndicate listings across Amazon US, Shopify DTC, Walmart, TikTok Shop &amp; eBay in 1 click.</p>
          </div>
          <div className="dock-item">
            <span className="dock-num tabular">04</span>
            <h3>Scale to 50%+ Factory Margins</h3>
            <p>Once a winner hits 15+ sales/week, switch to Alibaba Private Label FBA to double your profit.</p>
          </div>
        </div>
      </section>

      <section className="catalog-section">
        <div className="container">
          <div className="section-bar">
            <div>
              <span className="section-kicker">{products.length} VERIFIED MARKET WINNERS · ZERO JUNK PLACEHOLDERS</span>
              <h2>Researched Products Ready to Sell</h2>
            </div>
            <div className="preview-controls">
              <div className="filter-pills" role="group" aria-label="Filter products by category">
                {FILTER_CATEGORIES.map((cat) => {
                  const isCurrent = activeFilter === cat.key;
                  const label = cat.key === 'all' ? `All (${products.length})` : cat.label;
                  return (
                    <button
                      key={cat.key}
                      type="button"
                      className={`filter-btn ${isCurrent ? 'active' : ''}`}
                      aria-pressed={isCurrent ? 'true' : 'false'}
                      data-filter={cat.key}
                      onClick={() => onChangeFilter(cat.key)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <button type="button" className="btn-outline" id="catalog-open-research-btn" onClick={() => onSwitchView('research')}>
                📊 Why We Chose These {products.length}
              </button>
              <button
                type="button"
                className={`btn-outline ${pullLoading ? 'is-loading' : ''}`}
                id="pull-amazon-btn"
                disabled={pullLoading}
                title="Fetch Amazon's real status, price and issues for every product"
                onClick={async () => {
                  setPullLoading(true);
                  try {
                    await onPullAmazon();
                  } finally {
                    setPullLoading(false);
                  }
                }}
              >
                ↻ Pull from Amazon
              </button>
              <button
                type="button"
                className="btn-jet"
                id="sync-all-amazon-btn"
                disabled={syncAllLoading}
                title={
                  pendingSyncRows.length > 0
                    ? `Pushes ${pendingSyncRows.length} changed/new product(s) to Amazon:\n` +
                      pendingSyncRows
                        .map((r) => `• ${r.p.amazonListing?.amazonSku || r.p.sku}: ${r.msg || SYNC_STAGE_LABEL[r.stage]}`)
                        .join('\n')
                    : 'All products match Seller Central — click to re-verify'
                }
                onClick={async (e) => {
                  const trigger = e.currentTarget;
                  setSyncAllLoading(true);
                  try {
                    await onSyncAllAmazon(trigger);
                  } finally {
                    setSyncAllLoading(false);
                  }
                }}
              >
                {syncAllLoading
                  ? 'Syncing Changes to Amazon...'
                  : pendingSyncRows.length > 0
                    ? `⚡ Sync ${pendingSyncRows.length} Update${pendingSyncRows.length === 1 ? '' : 's'} to Amazon`
                    : `✓ All ${products.length} Synced`}
              </button>
            </div>
          </div>

          {renderSyncProgressSection()}

          <div className="catalog-toolbar-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 320px', maxWidth: '540px' }}>
              <input
                type="search"
                id="catalog-sku-search-input"
                aria-label="Search catalog by SKU, ASIN, CJ ID, or product title"
                placeholder="Search by SKU (e.g., AUR-GEN-147A), ASIN, CJ ID, or title..."
                value={catalogSearchQuery}
                onChange={(e) => setCatalogSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 14px',
                  borderRadius: '8px',
                  border: '1px solid #D4D4D8',
                  background: '#FFFFFF',
                  color: '#09090B',
                  fontSize: '0.86rem',
                  fontWeight: 500
                }}
              />
              {catalogSearchQuery && (
                <button
                  type="button"
                  onClick={() => setCatalogSearchQuery('')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #D4D4D8',
                    background: '#F4F4F5',
                    color: '#18181B',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Clear ({filteredProducts.length})
                </button>
              )}
            </div>
            <p id="amazon-sync-status-line" className={pullStatusLine.className || 'sync-status-line'} role="status" aria-live="polite" style={{ margin: 0 }}>
              {pullStatusLine.text}
            </p>
            <div className="layout-toggle-group" role="group" aria-label="Catalog layout view">
              <button
                type="button"
                id="layout-grid-btn"
                className={`btn-layout-toggle ${layoutMode === 'grid' ? 'active' : ''}`}
                aria-pressed={layoutMode === 'grid'}
                onClick={() => {
                  setLayoutMode('grid');
                  try {
                    localStorage.setItem('aurelius_catalog_layout', 'grid');
                  } catch (_) {}
                }}
                title="Grid View (3-Column Visual Cards)"
              >
                <span aria-hidden="true">⊞</span>
                <span>Grid</span>
              </button>
              <button
                type="button"
                id="layout-list-btn"
                className={`btn-layout-toggle ${layoutMode === 'list' ? 'active' : ''}`}
                aria-pressed={layoutMode === 'list'}
                onClick={() => {
                  setLayoutMode('list');
                  try {
                    localStorage.setItem('aurelius_catalog_layout', 'list');
                  } catch (_) {}
                }}
                title="List View (Executive Multi-Column Ledger)"
              >
                <span aria-hidden="true">☰</span>
                <span>List</span>
              </button>
              <button
                type="button"
                id="layout-iphone-btn"
                className={`btn-layout-toggle ${layoutMode === 'iphone' ? 'active' : ''}`}
                aria-pressed={layoutMode === 'iphone'}
                onClick={() => {
                  setLayoutMode('iphone');
                  try {
                    localStorage.setItem('aurelius_catalog_layout', 'iphone');
                  } catch (_) {}
                }}
                title="iPhone View (Compact iOS Inset Grouped List)"
              >
                <span aria-hidden="true">📱</span>
                <span>iPhone</span>
              </button>
            </div>
          </div>

          <div
            id="product-grid"
            className={
              layoutMode === 'grid'
                ? 'product-grid'
                : layoutMode === 'iphone'
                  ? 'iphone-list-wrapper'
                  : 'product-list-container'
            }
            role={layoutMode === 'list' ? 'region' : undefined}
            aria-label={layoutMode === 'list' ? 'Product catalog ledger' : undefined}
            tabIndex={layoutMode === 'list' ? 0 : undefined}
            aria-live="polite"
            aria-busy={scanning ? 'true' : undefined}
          >
            {layoutMode === 'iphone' && filteredProducts.length > 0 && (
              <div className="iphone-group-header">
                <span className="iphone-group-title tabular">
                  SHOWING {visibleProducts.length} OF {filteredProducts.length} PRODUCTS · SCROLL DOWN TO FETCH MORE
                </span>
                <span className="iphone-group-kicker tabular">
                  $0 Upfront US 3PL Dropship · Live Amazon SP-API
                </span>
              </div>
            )}
            {layoutMode === 'list' && filteredProducts.length > 0 && (
              <div className="product-ledger-head" aria-hidden="true">
                <div className="ledger-th">Product &amp; US 3PL Provenance</div>
                <div className="ledger-th">US Dropship</div>
                <div className="ledger-th">Sale Price &amp; List</div>
                <div className="ledger-th">Est. Net Profit</div>
                <div className="ledger-th">Amazon Status</div>
                <div className="ledger-th ledger-th-right">Pipeline Actions</div>
              </div>
            )}
            {scanning && (
              <div id="scan-skeleton-card" className="skeleton-card" aria-hidden="true">
                <div className="skeleton-block skeleton-stage"></div>
                <div className="skeleton-block skeleton-line-sm"></div>
                <div className="skeleton-block skeleton-line-lg"></div>
                <div className="skeleton-block skeleton-grid"></div>
                <div className="skeleton-block skeleton-btn"></div>
              </div>
            )}

            {filteredProducts.length === 0 ? (
              <div className="empty-state-card">
                <h3>No Matching Products in This View</h3>
                <p>No products match the active category filter or storefront assignment.</p>
                <button
                  type="button"
                  className="btn-jet"
                  id="reset-catalog-filter-btn"
                  onClick={() => {
                    setActiveStageFilter('all');
                    onResetFilterAndStore();
                  }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              visibleProducts.map((p) => {
                const econ = computeEconomics(p, activeStore);
                const dossier = p.researchDossier || {};
                const usCost = dossier.usDropshipCost || +(econ.landedUnitCost + 5.5).toFixed(2);
                const isAmzSynced = p.amazonListing?.amazonSyncStatus === 'ACTIVE' || p.amazonListing?.amazonSyncStatus === 'LIVE';
                const asin = p.amazonListing?.asin || p.asin || '';
                const meta = getAmazonStatusMeta(p);
                const amazonUrl = getAmazonProductUrl(p, sellerId);
                const sellerCentralUrl = getSellerCentralSkuUrl(p);
                const currentStageImg = activeCardImages[p.id] || p.images[0];
                const isCardSyncing = Boolean(syncingCardIds[p.id]);

                if (layoutMode === 'iphone') {
                  const errCount = p.issueSummary?.errorCount || 0;
                  const warnCount = p.issueSummary?.warningCount || 0;
                  const rankStr = String(p.rank || 1).padStart(2, '0');
                  const cjCode = dossier.cjSku || dossier.usDropshipSku || p.sku;
                  const fbmStock = p.amazonListing?.fbmQuantity ?? 50;
                  const rawStatusLabel = meta.isActive
                    ? 'Amazon Active'
                    : /review/i.test(meta.label)
                      ? 'Amazon Review'
                      : /attention|suppress|issue/i.test(meta.label)
                        ? 'Needs Attention'
                        : meta.label;

                  return (
                    <details
                      key={p.id}
                      id={`iphone-row-${p.id}`}
                      className="iphone-cell"
                      name="ios-catalog-accordion"
                    >
                      <summary className="iphone-cell-summary">
                        <span className="iphone-cell-num tabular">#{rankStr}</span>

                        <div
                          className="iphone-squircle"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenLightbox(p, e.currentTarget);
                          }}
                          title={`Click to view ${p.images.length} studio photos`}
                        >
                          <img src={currentStageImg} alt={p.title} loading="lazy" />
                          <span
                            className={`iphone-status-dot ${meta.isActive ? 'is-active' : errCount > 0 ? 'is-error' : 'is-pending'}`}
                            title={rawStatusLabel}
                          />
                        </div>

                        <div className="iphone-cell-main">
                          <div className="iphone-cell-title" title={p.title}>
                            {p.title}
                          </div>
                          <div className="iphone-cell-subtitle tabular">
                            <span className="iphone-sub-sku" title="CJ Dropshipping SKU / Model ID (Copy & paste into CJ search)">
                              CJ ID: <strong style={{ color: '#9E782F' }}>{cjCode}</strong>
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigator.clipboard?.writeText(String(cjCode || ''));
                                showToast(`Copied CJ ID: ${cjCode}`);
                              }}
                              style={{
                                background: '#FAF6EC',
                                color: '#09090B',
                                border: '1px solid #D9B668',
                                borderRadius: 4,
                                padding: '1px 6px',
                                fontSize: 10,
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                              title="Copy CJ ID to paste directly into CJ Dropshipping"
                            >
                              Copy CJ ID
                            </button>
                            <span className="iphone-sub-sep">·</span>
                            <span className="iphone-sub-sku">AMZ SKU: {p.amazonListing?.amazonSku || p.sku}</span>
                            {asin && (
                              <>
                                <span className="iphone-sub-sep">·</span>
                                <span className="iphone-sub-asin">{asin}</span>
                              </>
                            )}
                            <span className="iphone-sub-sep">·</span>
                            <span>🇺🇸 Cost {formatMoney(usCost)}</span>
                            <span className="iphone-sub-sep">·</span>
                            <span>{fbmStock} stock</span>
                            <span className="iphone-sub-sep">·</span>
                            <span className={`iphone-sub-status ${meta.isActive ? 'ok' : errCount > 0 ? 'err' : 'warn'}`}>
                              {rawStatusLabel}
                            </span>
                          </div>
                        </div>

                        <div className="iphone-cell-trailing">
                          <div className="iphone-price-stack tabular">
                            <span className="iphone-retail-price">{formatMoney(p.pricing.amazonRetailPrice)}</span>
                            {(() => {
                              const saleVal = Number(p.pricing.amazonRetailPrice || 39.99);
                              const listVal =
                                p.pricing.listPrice && p.pricing.listPrice > saleVal
                                  ? Number(p.pricing.listPrice)
                                  : +(Math.ceil(saleVal * 1.28) - 0.01).toFixed(2);
                              const pctVal = p.pricing.discountPct || Math.round(((listVal - saleVal) / listVal) * 100);
                              return (
                                <div className="iphone-sale-badge">
                                  <span className="iphone-strikethrough-price">{formatMoney(listVal)}</span>
                                  <span className="iphone-discount-tag">-{pctVal}% SALE</span>
                                </div>
                              );
                            })()}
                            <span className="iphone-niche-micro truncate">{p.niche}</span>
                          </div>

                          <div className="iphone-profit-pill tabular" title="Estimated Net Profit & Margin per Unit">
                            <span className="iphone-profit-amt">+{formatMoney(econ.netProfitPerUnit)}</span>
                            <span className="iphone-profit-pct">{econ.netMarginPct}%</span>
                          </div>

                          <button
                            type="button"
                            className={`iphone-sync-chip ${isAmzSynced ? 'synced' : 'pending'}`}
                            disabled={isCardSyncing}
                            title={isAmzSynced ? 'Synced on Amazon (Click to re-sync)' : 'Sync to Amazon Seller Central'}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const trigger = e.currentTarget;
                              setSyncingCardIds((prev) => ({ ...prev, [p.id]: true }));
                              try {
                                await onSyncSingleProductCard(p, trigger);
                              } finally {
                                setSyncingCardIds((prev) => ({ ...prev, [p.id]: false }));
                              }
                            }}
                          >
                            {isCardSyncing ? '...' : isAmzSynced ? '✓ Synced' : '⚡ Sync'}
                          </button>

                          <span className="iphone-chevron" aria-hidden="true">
                            ›
                          </span>
                        </div>
                      </summary>

                      <div className="iphone-cell-drawer">
                        <div className="iphone-drawer-metrics tabular">
                          <div className="iphone-dm-item">
                            <span className="iphone-dm-label">Sale Price vs. List</span>
                            <span className="iphone-dm-val">
                              {formatMoney(p.pricing.amazonRetailPrice)}{' '}
                              <span style={{ textDecoration: 'line-through', color: '#71717A', fontWeight: 500 }}>
                                {formatMoney(p.pricing.listPrice || +(Math.ceil(p.pricing.amazonRetailPrice * 1.28) - 0.01).toFixed(2))}
                              </span>{' '}
                              <span style={{ color: '#DC2626', fontWeight: 800 }}>
                                (-{p.pricing.discountPct || 21}%)
                              </span>
                            </span>
                          </div>
                          <div className="iphone-dm-item">
                            <span className="iphone-dm-label">US Dropship ($0 MOQ)</span>
                            <span className="iphone-dm-val">{formatMoney(usCost)}</span>
                          </div>
                          <div className="iphone-dm-item">
                            <span className="iphone-dm-label">Bulk FBA Landed</span>
                            <span className="iphone-dm-val">{formatMoney(econ.landedUnitCost)}</span>
                          </div>
                          <div className="iphone-dm-item">
                            <span className="iphone-dm-label">Search Demand</span>
                            <span className="iphone-dm-val">
                              {dossier.monthlySearchVolume || '54,000 / mo'} ({dossier.yoyGrowth || '+42% YoY'})
                            </span>
                          </div>
                          <div className="iphone-dm-item">
                            <span className="iphone-dm-label">CJ US SKU</span>
                            <span className="iphone-dm-val">{cjCode}</span>
                          </div>
                        </div>

                        <div className="iphone-drawer-actions">
                          <button
                            type="button"
                            className="btn-jet iphone-act-btn primary"
                            onClick={() => onSelectProductAndView(p.id, 'launch')}
                          >
                            ⚡ Launch Pipeline →
                          </button>
                          <button
                            type="button"
                            className={`iphone-act-btn ${editingCardId === p.id ? 'active' : ''}`}
                            onClick={() => {
                              if (editingCardId === p.id) {
                                setEditingCardId(null);
                              } else {
                                setEditingCardId(p.id);
                                setQuickPrice(String(p.pricing.amazonRetailPrice));
                                setQuickQty(String(fbmStock));
                              }
                            }}
                          >
                            {editingCardId === p.id ? '✕ Close Edit' : '✎ Edit Price & Stock'}
                          </button>
                          <button
                            type="button"
                            className="iphone-act-btn"
                            onClick={(e) => onOpenLightbox(p, e.currentTarget)}
                          >
                            🖼 {p.images.length} Photos
                          </button>
                          <button
                            type="button"
                            className="iphone-act-btn"
                            onClick={() => onSelectProductAndView(p.id, 'research', true)}
                          >
                            📊 Dossier
                          </button>
                          <button
                            type="button"
                            className="iphone-act-btn"
                            onClick={(e) => onOpenIssues(p.id, e.currentTarget)}
                          >
                            {errCount > 0 ? `⚠ ${errCount} Error(s)` : warnCount > 0 ? `! ${warnCount} Hold` : '✓ Channel Health'}
                          </button>
                          <a
                            href={amazonUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="iphone-act-btn"
                          >
                            🛒 Amazon ↗
                          </a>
                          <a
                            href={dossier.usDropshipUrl || dossier.cjExactUrl || 'https://cjdropshipping.com'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="iphone-act-btn"
                          >
                            📦 CJ Supplier ↗
                          </a>
                          <a
                            href={sellerCentralUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="iphone-act-btn"
                          >
                            Seller Central ↗
                          </a>
                        </div>

                        {editingCardId === p.id && (
                          <div className="list-quick-edit-drawer" style={{ borderRadius: '10px', marginTop: '10px', border: '1px solid #EFE4CC' }}>
                            <div className="list-qe-left">
                              <span className="list-qe-title">Quick Edit · {p.amazonListing?.amazonSku || p.sku}</span>
                              <label className="list-qe-field">
                                <span>
                                  Sale Price ($) ·{' '}
                                  <strong style={{ color: '#71717A', textDecoration: 'line-through' }}>
                                    List {formatMoney(+(Math.ceil((parseFloat(quickPrice) || p.pricing.amazonRetailPrice) * 1.28) - 0.01).toFixed(2))}
                                  </strong>{' '}
                                  <strong style={{ color: '#DC2626' }}>(-21% Off)</strong>
                                </span>
                                <input
                                  type="number"
                                  step="0.50"
                                  min="1.00"
                                  className="tabular"
                                  value={quickPrice}
                                  onChange={(e) => setQuickPrice(e.target.value)}
                                />
                              </label>
                              <label className="list-qe-field">
                                <span>FBM Stock Qty</span>
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  className="tabular"
                                  value={quickQty}
                                  onChange={(e) => setQuickQty(e.target.value)}
                                />
                              </label>
                            </div>
                            <div className="list-qe-actions">
                              <button
                                type="button"
                                className="btn-outline btn-xs"
                                disabled={quickSavingId === p.id}
                                onClick={async () => {
                                  setQuickSavingId(p.id);
                                  try {
                                    await onQuickUpdateProduct(p.id, {
                                      amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                      fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                      syncNow: false
                                    });
                                    setEditingCardId(null);
                                  } finally {
                                    setQuickSavingId(null);
                                  }
                                }}
                              >
                                Save Draft
                              </button>
                              <button
                                type="button"
                                className="btn-jet btn-xs"
                                disabled={quickSavingId === p.id}
                                onClick={async () => {
                                  setQuickSavingId(p.id);
                                  try {
                                    await onQuickUpdateProduct(p.id, {
                                      amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                      fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                      syncNow: true
                                    });
                                    setEditingCardId(null);
                                  } finally {
                                    setQuickSavingId(null);
                                  }
                                }}
                              >
                                {quickSavingId === p.id ? 'Patching…' : '⚡ Fast PATCH Amazon'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </details>
                  );
                }

                if (layoutMode === 'list') {
                  const errCount = p.issueSummary?.errorCount || 0;
                  const warnCount = p.issueSummary?.warningCount || 0;
                  const rankStr = String(p.rank || 1).padStart(2, '0');
                  const cjCode = dossier.cjSku || dossier.usDropshipSku || p.sku;
                  const fbmStock = p.amazonListing?.fbmQuantity ?? 50;
                  const rawStatusLabel = meta.isActive
                    ? 'Amazon Active'
                    : /review/i.test(meta.label)
                      ? 'Amazon Review'
                      : /attention|suppress|issue/i.test(meta.label)
                        ? 'Needs Attention'
                        : meta.label;

                  return (
                    <article key={p.id} className="product-list-row" id={`list-row-${p.id}`}>
                      {/* Column 1: Product Thumbnail + Identity + Provenance Chips */}
                      <div className="list-col-product">
                        <div
                          className="product-list-thumb"
                          onClick={(e) => onOpenLightbox(p, e.currentTarget)}
                          title={`Click to view ${p.images.length} studio photos for ${p.title}`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpenLightbox(p, e.currentTarget);
                            }
                          }}
                        >
                          <span className="list-rank-tag tabular">#{rankStr}</span>
                          <img src={currentStageImg} alt={p.title} loading="lazy" />
                          <span className="list-photo-count tabular" aria-hidden="true">
                            {p.images.length}
                          </span>
                        </div>

                        <div className="product-list-info">
                          <div className="product-list-eyebrow">
                            <span className="list-niche-kicker truncate">{p.niche}</span>
                            <span className="list-demand-pill tabular">
                              {dossier.monthlySearchVolume || '54,000 / mo'} · {dossier.yoyGrowth || '+42% YoY'}
                            </span>
                          </div>

                          <h3 className="product-list-title" title={p.title}>
                            {p.title}
                          </h3>

                          <div className="product-list-chips">
                            <span className="list-chip list-chip-sku tabular" title="Aurelius / Seller Central SKU">
                              <strong>{p.amazonListing?.amazonSku || p.sku}</strong>
                            </span>
                            {asin ? (
                              <a
                                href={amazonUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="list-chip list-chip-asin tabular"
                                title={`View ASIN ${asin} live on Amazon.com`}
                              >
                                <strong>{asin}</strong> <span aria-hidden="true">↗</span>
                              </a>
                            ) : (
                              <span className="list-chip list-chip-muted tabular">ASIN Pending</span>
                            )}
                            <span className="list-chip list-chip-warehouse" title="Ships from CJ US Domestic Warehouse via USPS Ground Advantage (2–3 day delivery)">
                              🇺🇸 US 3PL
                            </span>
                            <a
                              href={dossier.usDropshipUrl || dossier.cjExactUrl || 'https://cjdropshipping.com'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="list-chip list-chip-supplier"
                              title={`View supplier on CJdropshipping (CJ SKU: ${cjCode})`}
                            >
                              CJ Supplier <span aria-hidden="true">↗</span>
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Column 2: US Dropship Cost */}
                      <div className="list-col-metric" data-label="US Dropship">
                        <span className="list-metric-val tabular">{formatMoney(usCost)}</span>
                        <span className="list-metric-sub">$0 MOQ · Landed</span>
                        <span className="list-metric-fba tabular">Bulk FBA: {formatMoney(econ.landedUnitCost)}</span>
                      </div>

                      {/* Column 3: Sale Price, List Price & Stock */}
                      <div className="list-col-metric" data-label="Sale Price & List">
                        <div className="list-price-stack">
                          <span className="list-metric-val tabular">{formatMoney(p.pricing.amazonRetailPrice)}</span>
                          {(() => {
                            const saleVal = Number(p.pricing.amazonRetailPrice || 39.99);
                            const listVal =
                              p.pricing.listPrice && p.pricing.listPrice > saleVal
                                ? Number(p.pricing.listPrice)
                                : +(Math.ceil(saleVal * 1.28) - 0.01).toFixed(2);
                            const pctVal = p.pricing.discountPct || Math.round(((listVal - saleVal) / listVal) * 100);
                            return (
                              <div className="list-sale-tag">
                                <span className="list-strike-price tabular">{formatMoney(listVal)}</span>
                                <span className="list-discount-pill">-{pctVal}% SALE</span>
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          type="button"
                          className={`list-stock-pill-btn tabular ${editingCardId === p.id ? 'active' : ''}`}
                          title="Quick-edit retail price and FBM stock"
                          onClick={() => {
                            if (editingCardId === p.id) {
                              setEditingCardId(null);
                            } else {
                              setEditingCardId(p.id);
                              setQuickPrice(String(p.pricing.amazonRetailPrice));
                              setQuickQty(String(fbmStock));
                            }
                          }}
                        >
                          {editingCardId === p.id ? '✕ Close Edit' : `${fbmStock} in stock · Edit`}
                        </button>
                      </div>

                      {/* Column 4: Net Profit & Margin */}
                      <div className="list-col-metric" data-label="Est. Net Profit">
                        <span className="list-metric-val gold tabular">+{formatMoney(econ.netProfitPerUnit)}</span>
                        <span className="list-margin-badge tabular">{econ.netMarginPct}% margin</span>
                      </div>

                      {/* Column 5: Amazon Status & Channel Health */}
                      <div className="list-col-status" data-label="Amazon Status">
                        <a
                          href={amazonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`list-status-badge ${meta.isActive ? 'is-active' : 'is-pending'}`}
                          title={`${meta.label} (Click to open on Amazon ↗)`}
                        >
                          <span className={`status-indicator-dot ${meta.isActive ? 'dot-active' : 'dot-pending'}`}></span>
                          <span className="list-status-text">{rawStatusLabel}</span>
                          <span aria-hidden="true">↗</span>
                        </a>

                        <div className="list-status-sub">
                          <button
                            type="button"
                            className={`list-health-btn tabular ${errCount > 0 ? 'is-error' : warnCount > 0 ? 'is-warn' : 'is-ok'}`}
                            onClick={(e) => onOpenIssues(p.id, e.currentTarget)}
                            title="Inspect channel health and listing issues"
                          >
                            {errCount > 0
                              ? `⚠ ${errCount} error${errCount > 1 ? 's' : ''}`
                              : warnCount > 0
                                ? `! ${warnCount} hold`
                                : '✓ 100% Ready'}
                          </button>
                          <span className="list-sep-dot" aria-hidden="true">·</span>
                          <a
                            href={sellerCentralUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="list-sc-link"
                            title="Manage SKU in Amazon Seller Central"
                          >
                            Seller Central ↗
                          </a>
                        </div>
                      </div>

                      {/* Column 6: Pipeline Actions */}
                      <div className="list-col-actions" data-label="Actions">
                        <div className="list-action-top">
                          <button
                            type="button"
                            className="btn-jet list-btn-launch"
                            onClick={() => onSelectProductAndView(p.id, 'launch')}
                          >
                            Launch <span aria-hidden="true">→</span>
                          </button>
                          <button
                            type="button"
                            className={`list-btn-sync ${isAmzSynced ? 'synced' : 'pending'}`}
                            disabled={isCardSyncing}
                            title={isAmzSynced ? 'Synced with Amazon Seller Central (Click to re-sync)' : 'Push listing to Amazon Seller Central'}
                            onClick={async (e) => {
                              const trigger = e.currentTarget;
                              setSyncingCardIds((prev) => ({ ...prev, [p.id]: true }));
                              try {
                                await onSyncSingleProductCard(p, trigger);
                              } finally {
                                setSyncingCardIds((prev) => ({ ...prev, [p.id]: false }));
                              }
                            }}
                          >
                            {isCardSyncing ? '...' : isAmzSynced ? '✓ Synced' : '⚡ Sync'}
                          </button>
                        </div>
                        <div className="list-action-bottom">
                          <button
                            type="button"
                            className="list-btn-secondary"
                            onClick={() => onSelectProductAndView(p.id, 'research', true)}
                            title="Open Full Market Research Dossier"
                          >
                            📊 Dossier
                          </button>
                          <button
                            type="button"
                            className="list-btn-secondary tabular"
                            disabled={isCardSyncing}
                            title={`Download Amazon Flat-File .TSV for ${p.title}`}
                            onClick={async (e) => {
                              const trigger = e.currentTarget;
                              setSyncingCardIds((prev) => ({ ...prev, [p.id]: true }));
                              try {
                                await onSyncSingleProductCard(p, trigger);
                              } finally {
                                setSyncingCardIds((prev) => ({ ...prev, [p.id]: false }));
                              }
                            }}
                          >
                            📥 .TSV
                          </button>
                        </div>
                      </div>

                      {editingCardId === p.id && (
                        <div className="list-quick-edit-drawer" role="region" aria-label={`Quick edit price and stock for ${p.title}`}>
                          <div className="list-qe-left">
                            <span className="list-qe-title">Quick Edit · {p.amazonListing?.amazonSku || p.sku}</span>
                            <label className="list-qe-field">
                              <span>
                                Sale Price ($) ·{' '}
                                <strong style={{ color: '#71717A', textDecoration: 'line-through' }}>
                                  List {formatMoney(+(Math.ceil((parseFloat(quickPrice) || p.pricing.amazonRetailPrice) * 1.28) - 0.01).toFixed(2))}
                                </strong>{' '}
                                <strong style={{ color: '#DC2626' }}>(-21% Off)</strong>
                              </span>
                              <input
                                type="number"
                                step="0.50"
                                min="1.00"
                                className="tabular"
                                value={quickPrice}
                                onChange={(e) => setQuickPrice(e.target.value)}
                              />
                            </label>
                            <label className="list-qe-field">
                              <span>FBM Stock Qty</span>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                className="tabular"
                                value={quickQty}
                                onChange={(e) => setQuickQty(e.target.value)}
                              />
                            </label>
                          </div>
                          <div className="list-qe-actions">
                            <button
                              type="button"
                              className="btn-outline btn-xs"
                              disabled={quickSavingId === p.id}
                              onClick={async () => {
                                setQuickSavingId(p.id);
                                try {
                                  await onQuickUpdateProduct(p.id, {
                                    amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                    fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                    syncNow: false
                                  });
                                  setEditingCardId(null);
                                } finally {
                                  setQuickSavingId(null);
                                }
                              }}
                            >
                              Save Draft
                            </button>
                            <button
                              type="button"
                              className="btn-jet btn-xs"
                              disabled={quickSavingId === p.id}
                              onClick={async () => {
                                setQuickSavingId(p.id);
                                try {
                                  await onQuickUpdateProduct(p.id, {
                                    amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                    fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                    syncNow: true
                                  });
                                  setEditingCardId(null);
                                } finally {
                                  setQuickSavingId(null);
                                }
                              }}
                            >
                              {quickSavingId === p.id ? 'Patching…' : '⚡ Fast PATCH Amazon'}
                            </button>
                            <button
                              type="button"
                              className="list-qe-cancel"
                              onClick={() => setEditingCardId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                }

                return (
                  <article key={p.id} className="product-card" id={`card-${p.id}`}>
                    <div className="card-photo-stage">
                      <img id={`stage-img-${p.id}`} src={currentStageImg} alt={p.title} loading="lazy" />
                      <div className="card-photo-badges">
                        <a
                          href={amazonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`card-status-badge ${meta.isActive ? 'is-active' : 'is-pending'}`}
                          title={`${meta.label} (Click to view listing on Amazon ↗)`}
                        >
                          <span className={`status-indicator-dot ${meta.isActive ? 'dot-active' : 'dot-pending'}`}></span>
                          <span>{meta.isActive ? 'Amazon Active' : meta.label}</span>
                          <span className="badge-arrow" aria-hidden="true">
                            ↗
                          </span>
                        </a>
                        <div className="card-price-stack">
                          <span className="card-price-pill tabular">{formatMoney(p.pricing.amazonRetailPrice)}</span>
                          {(() => {
                            const saleVal = Number(p.pricing.amazonRetailPrice || 39.99);
                            const listVal =
                              p.pricing.listPrice && p.pricing.listPrice > saleVal
                                ? Number(p.pricing.listPrice)
                                : +(Math.ceil(saleVal * 1.28) - 0.01).toFixed(2);
                            const pctVal = p.pricing.discountPct || Math.round(((listVal - saleVal) / listVal) * 100);
                            return (
                              <div className="card-sale-discount">
                                <span className="card-strike-price tabular">{formatMoney(listVal)}</span>
                                <span className="card-discount-badge">-{pctVal}% SALE</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="card-filmstrip" role="group" aria-label="Product angle thumbnails">
                      {p.images.slice(0, 5).map((url, idx) => {
                        const isLast = idx === 4 && p.images.length > 5;
                        const isActiveThumb = currentStageImg === url;
                        const label = isLast
                          ? `Open full ${p.images.length}-photo gallery for ${p.title}`
                          : `View photo ${idx + 1} of ${p.images.length} for ${p.title}`;

                        return (
                          <button
                            key={url + idx}
                            type="button"
                            className={`thumb-btn ${isActiveThumb ? 'active' : ''}`}
                            data-card-id={p.id}
                            data-img-url={url}
                            data-open-lightbox={isLast ? 'true' : 'false'}
                            aria-label={label}
                            aria-pressed={isActiveThumb ? 'true' : 'false'}
                            onClick={(e) => {
                              if (isLast) {
                                onOpenLightbox(p, e.currentTarget);
                              } else {
                                setActiveCardImages((prev) => ({ ...prev, [p.id]: url }));
                              }
                            }}
                          >
                            <img src={url} alt="" loading="lazy" />
                            {isLast && (
                              <span className="thumb-more-overlay tabular" aria-hidden="true">
                                +{p.images.length - 4}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className="card-body">
                      <div className="card-header-meta">
                        <span className="card-niche-tag truncate">{p.niche}</span>
                        <span className="card-demand-tag tabular">
                          {dossier.monthlySearchVolume || '54,000 / mo'} · {dossier.yoyGrowth || '+42% YoY'}
                        </span>
                      </div>

                      <h3 className="card-title line-clamp-2" title={p.title}>
                        {p.title}
                      </h3>

                      <div className="arch-spec-grid">
                        <div className="arch-spec-cell">
                          <span className="spec-micro-label">US DROPSHIP</span>
                          <span className="spec-val tabular">{formatMoney(usCost)}</span>
                          <span className="spec-sub-note">$0 MOQ · Landed</span>
                        </div>
                        <div className="arch-spec-cell">
                          <span className="spec-micro-label">SALE &amp; LIST PRICE</span>
                          <span className="spec-val tabular" style={{ display: 'flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
                            <span>{formatMoney(p.pricing.amazonRetailPrice)}</span>
                            <span style={{ fontSize: '0.72rem', color: '#71717A', textDecoration: 'line-through', fontWeight: 500 }}>
                              {formatMoney(p.pricing.listPrice || +(Math.ceil(p.pricing.amazonRetailPrice * 1.28) - 0.01).toFixed(2))}
                            </span>
                            <span
                              style={{
                                fontSize: '0.66rem',
                                fontWeight: 800,
                                color: '#FFFFFF',
                                background: '#DC2626',
                                padding: '1px 5px',
                                borderRadius: 4,
                                lineHeight: 1.25
                              }}
                            >
                              -{p.pricing.discountPct || 21}%
                            </span>
                          </span>
                          <button
                            type="button"
                            className="spec-quick-edit-btn tabular"
                            title="Quick-edit sale price & FBM stock on this card"
                            onClick={() => {
                              if (editingCardId === p.id) {
                                setEditingCardId(null);
                              } else {
                                setEditingCardId(p.id);
                                setQuickPrice(String(p.pricing.amazonRetailPrice));
                                setQuickQty(String(p.amazonListing?.fbmQuantity ?? 50));
                              }
                            }}
                          >
                            {editingCardId === p.id ? '✕ Close' : `✎ Edit · ${p.amazonListing?.fbmQuantity ?? 50} in stock`}
                          </button>
                        </div>
                        <div className="arch-spec-cell">
                          <span className="spec-micro-label">EST. NET PROFIT</span>
                          <span className="spec-val gold tabular">+{formatMoney(econ.netProfitPerUnit)}</span>
                          <span className="spec-sub-pill tabular">{econ.netMarginPct}% margin</span>
                        </div>
                      </div>

                      {editingCardId === p.id && (
                        <div className="card-quick-edit-panel" role="region" aria-label={`Quick edit price and stock for ${p.title}`}>
                          <div className="quick-edit-fields">
                            <label className="quick-edit-field">
                              <span>
                                Sale Price ($) ·{' '}
                                <span style={{ textDecoration: 'line-through', color: '#71717A' }}>
                                  List {formatMoney(+(Math.ceil((parseFloat(quickPrice) || p.pricing.amazonRetailPrice) * 1.28) - 0.01).toFixed(2))}
                                </span>{' '}
                                <span style={{ color: '#DC2626', fontWeight: 700 }}>(-21%)</span>
                              </span>
                              <input
                                type="number"
                                step="0.50"
                                min="1.00"
                                className="tabular"
                                value={quickPrice}
                                onChange={(e) => setQuickPrice(e.target.value)}
                              />
                            </label>
                            <label className="quick-edit-field">
                              <span>FBM Qty</span>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                className="tabular"
                                value={quickQty}
                                onChange={(e) => setQuickQty(e.target.value)}
                              />
                            </label>
                          </div>
                          <div className="quick-edit-actions">
                            <button
                              type="button"
                              className="btn-outline btn-xs"
                              disabled={quickSavingId === p.id}
                              title="Save locally and queue for batch sync"
                              onClick={async () => {
                                setQuickSavingId(p.id);
                                try {
                                  await onQuickUpdateProduct(p.id, {
                                    amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                    fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                    syncNow: false
                                  });
                                  setEditingCardId(null);
                                } finally {
                                  setQuickSavingId(null);
                                }
                              }}
                            >
                              Save Draft
                            </button>
                            <button
                              type="button"
                              className="btn-jet btn-xs"
                              disabled={quickSavingId === p.id}
                              title="Send surgical offer PATCH to Amazon Seller Central immediately"
                              onClick={async () => {
                                setQuickSavingId(p.id);
                                try {
                                  await onQuickUpdateProduct(p.id, {
                                    amazonRetailPrice: parseFloat(quickPrice) || p.pricing.amazonRetailPrice,
                                    fbmQuantity: Math.max(0, parseInt(quickQty, 10) || 0),
                                    syncNow: true
                                  });
                                  setEditingCardId(null);
                                } finally {
                                  setQuickSavingId(null);
                                }
                              }}
                            >
                              {quickSavingId === p.id ? 'Patching…' : '⚡ Fast PATCH Amazon'}
                            </button>
                          </div>
                        </div>
                      )}

                      {p.amazonListing?.syncState === 'drift' && editingCardId !== p.id && (
                        <div className="card-drift-banner">
                          <span className="card-drift-text tabular">⚡ {formatSyncDiffSummary(p)}</span>
                          <button
                            type="button"
                            className="card-drift-patch-btn"
                            disabled={quickSavingId === p.id}
                            onClick={async () => {
                              setQuickSavingId(p.id);
                              try {
                                await onQuickUpdateProduct(p.id, {
                                  amazonRetailPrice: Number(p.pricing.amazonRetailPrice),
                                  fbmQuantity: Number(p.amazonListing?.fbmQuantity ?? 50),
                                  syncNow: true
                                });
                              } finally {
                                setQuickSavingId(null);
                              }
                            }}
                          >
                            {quickSavingId === p.id ? 'Patching…' : 'Patch Now →'}
                          </button>
                        </div>
                      )}

                      <div className="card-supplier-strip">
                        <div className="supplier-provenance truncate" title="Ships from CJ US Warehouse via USPS Ground Advantage">
                          <span className="supplier-flag" aria-hidden="true">
                            🇺🇸
                          </span>
                          <span className="supplier-name">CJ US Warehouse</span>
                          <span className="supplier-divider">·</span>
                          <span className="supplier-shipping">Free 2–3d USPS</span>
                          <span className="supplier-divider">·</span>
                          <span className="supplier-sku tabular">SKU: {dossier.cjSku || dossier.usDropshipSku || p.sku}</span>
                        </div>
                        <a
                          href={dossier.usDropshipUrl || dossier.cjExactUrl || 'https://cjdropshipping.com'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="supplier-link-btn"
                          title="View exact product on CJdropshipping"
                        >
                          <span>CJ Supplier</span>
                          <span aria-hidden="true">↗</span>
                        </a>
                      </div>

                      {renderIssuePill(p)}

                      <div className="card-amazon-dock">
                        <a
                          href={amazonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-view-on-amazon"
                          aria-label={`View ${p.title} on Amazon in a new tab`}
                        >
                          <span className="btn-amz-brand">
                            <svg
                              className="amz-cart-svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <circle cx="9" cy="21" r="1"></circle>
                              <circle cx="20" cy="21" r="1"></circle>
                              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                            </svg>
                            <span>View on Amazon</span>
                          </span>
                          <span className="btn-amz-badge-wrap">
                            {asin ? (
                              <span className="btn-amz-asin tabular">{asin}</span>
                            ) : (
                              <span className="btn-amz-asin tabular" title="Amazon is assigning a new ASIN">
                                ASIN pending
                              </span>
                            )}
                            <span className="btn-amz-arrow" aria-hidden="true">
                              ↗
                            </span>
                          </span>
                        </a>
                        <div className="card-amz-subline">
                          <span className="card-amz-seller-badge truncate">
                            <span className={`seller-dot ${meta.isActive ? '' : 'pending'}`}></span>
                            <span>
                              Listing: <strong>{meta.isActive ? 'Active & Live' : meta.label}</strong> ({p.amazonListing?.amazonSku || p.sku})
                            </span>
                          </span>
                          <a
                            href={sellerCentralUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-sc-direct"
                            title="Manage in Amazon Seller Central"
                          >
                            Seller Central ↗
                          </a>
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          type="button"
                          className="btn-jet launch-product-btn"
                          data-product-id={p.id}
                          onClick={() => onSelectProductAndView(p.id, 'launch')}
                        >
                          <span>⚡ Launch Pipeline</span>
                          <span aria-hidden="true">→</span>
                        </button>
                        <button
                          type="button"
                          className="btn-outline open-research-card-btn"
                          data-product-id={p.id}
                          onClick={() => onSelectProductAndView(p.id, 'research', true)}
                        >
                          <span>📊 Research Dossier</span>
                        </button>
                        <button
                          type="button"
                          className={`btn-outline sync-card-amz-btn icon-tsv-btn ${isAmzSynced ? 'synced' : ''}`}
                          data-product-id={p.id}
                          disabled={isCardSyncing}
                          title={`Download Amazon TSV Feed for ${p.title}`}
                          aria-label="Download Amazon TSV feed"
                          onClick={async (e) => {
                            const trigger = e.currentTarget;
                            setSyncingCardIds((prev) => ({ ...prev, [p.id]: true }));
                            try {
                              await onSyncSingleProductCard(p, trigger);
                            } finally {
                              setSyncingCardIds((prev) => ({ ...prev, [p.id]: false }));
                            }
                          }}
                        >
                          {isCardSyncing ? (
                            'Syncing...'
                          ) : (
                            <>
                              <span aria-hidden="true">📥</span>
                              <span>.TSV</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {filteredProducts.length > 0 && (
            <div
              ref={loadMoreSentinelRef}
              id="catalog-infinite-scroll-dock"
              aria-live="polite"
              style={{
                marginTop: 20,
                padding: '18px 22px',
                background: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: 12,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240, flex: '1 1 260px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: hasMoreProducts ? '#9E782F' : '#15803D',
                      display: 'inline-block'
                    }}
                  />
                  <strong style={{ fontSize: 13, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                    Showing {visibleProducts.length} of {filteredProducts.length} products
                  </strong>
                  <span style={{ fontSize: 12, color: '#71717A', fontVariantNumeric: 'tabular-nums' }}>
                    ({Math.round((visibleProducts.length / filteredProducts.length) * 100)}%)
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    background: '#F3F4F6',
                    borderRadius: 999,
                    overflow: 'hidden',
                    maxWidth: 360
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.round((visibleProducts.length / filteredProducts.length) * 100))}%`,
                      height: '100%',
                      background: hasMoreProducts ? '#09090B' : '#15803D',
                      transition: 'width 220ms ease'
                    }}
                  />
                </div>
                <span style={{ fontSize: 12, color: '#52525B' }}>
                  {hasMoreProducts
                    ? isFetchingMore
                      ? 'Fetching next batch of products…'
                      : `Scroll down to auto-fetch ${Math.min(CATALOG_PAGE_SIZE, filteredProducts.length - visibleProducts.length)} more products, or use the controls on the right.`
                    : `All ${filteredProducts.length} matching products are loaded in this view.`}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {hasMoreProducts ? (
                  <>
                    <button
                      type="button"
                      className="btn-jet"
                      id="fetch-more-products-btn"
                      onClick={fetchNextBatch}
                      disabled={isFetchingMore}
                      style={{ minHeight: 42, padding: '8px 16px', fontSize: 13 }}
                    >
                      {isFetchingMore
                        ? 'Loading…'
                        : `Load ${Math.min(CATALOG_PAGE_SIZE, filteredProducts.length - visibleProducts.length)} More ↓`}
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      id="show-all-products-btn"
                      onClick={() => setVisibleCount(filteredProducts.length)}
                      style={{ minHeight: 42, padding: '8px 16px', fontSize: 13 }}
                    >
                      Show All ({filteredProducts.length})
                    </button>
                  </>
                ) : (
                  filteredProducts.length > CATALOG_PAGE_SIZE && (
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => {
                        setVisibleCount(CATALOG_PAGE_SIZE);
                        document.getElementById('catalog-section')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      style={{ minHeight: 42, padding: '8px 16px', fontSize: 13 }}
                    >
                      Collapse to Top {CATALOG_PAGE_SIZE} ↑
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
